import { TextareaHTMLAttributes, useId, useState } from 'react';

/** Textarea — multi-line text field, matches Input styling. */
export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  /** Quiet helper line under the label — e.g. who actually sees this field. */
  hint?: string;
}

/**
 * Textarea — multi-line text field. Matches Input styling; min 3 rows.
 */
export function Textarea({ label, error, hint, id: idProp, rows = 3, style, ...props }: TextareaProps) {
  // Generate an id when the caller doesn't pass one, so `htmlFor` always has
  // a target. Without this a label with no `id` renders as a floating text
  // node and the control is unlabelled to assistive tech — which was true of
  // every field on the host profile.
  const autoId = useId();
  const id = idProp ?? autoId;
  const [focus, setFocus] = useState(false);
  const errored = !!error;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label ? (
        <label htmlFor={id} style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500, color: 'var(--text-soft)' }}>{label}</label>
      ) : null}
      {hint ? (
        <p id={`${id}-hint`} style={{ fontFamily: 'var(--font-sans)', fontSize: 11.5, color: 'var(--text-dim)', margin: '-2px 0 0' }}>{hint}</p>
      ) : null}
      <textarea
        id={id}
        aria-describedby={hint ? `${id}-hint` : undefined}
        rows={rows}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{
          width: '100%',
          fontFamily: 'var(--font-sans)',
          fontSize: 15,
          lineHeight: 1.6,
          color: 'var(--text)',
          background: 'var(--bg)',
          border: `1px solid ${errored ? 'rgb(var(--crimson))' : focus ? 'rgb(var(--gold))' : 'var(--line)'}`,
          borderRadius: 'var(--r-md)',
          padding: '10px 14px',
          outline: 'none',
          resize: 'vertical',
          boxShadow: focus ? `0 0 0 3px rgb(var(--gold) / .2)` : 'none',
          transition: 'border-color .2s, box-shadow .2s',
          ...style,
        }}
        {...props}
      />
      {errored ? <p style={{ fontFamily: 'var(--font-sans)', fontSize: 11, color: 'rgb(var(--crimson))' }}>{error}</p> : null}
    </div>
  );
}

export default Textarea;
